import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Component } from '@/types';
import { cn } from '@/lib/utils';
import {
  CHARCOAL,
  SEAFOAM_GREEN,
  MID_GRAY,
  WHITE,
  BORDER,
  WARNING,
} from '@/styles/tokens';
import {
  formatPropertyValue,
  formatStyleValue,
  getRelevantProperties,
  getRelevantStyles,
  getComponentTypeLabel,
  truncateId,
  formatPositioningStrategy,
  hasDataConnection,
  getDataConnectionInfo,
  isDataCapableType,
  formatRefreshInterval,
} from '@/utils/PropertyFormatters';
import { calculateTooltipPosition, TooltipPlacement } from '@/utils/TooltipPositioning';

/**
 * Small database/cylinder icon (12x12) for data connection indicator
 */
const DataIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <ellipse cx="6" cy="3" rx="4.5" ry="1.8" stroke={color} strokeWidth="1.2" fill="none" />
    <path d="M1.5 3v6c0 1 2 1.8 4.5 1.8S10.5 10 10.5 9V3" stroke={color} strokeWidth="1.2" fill="none" />
    <path d="M1.5 6c0 1 2 1.8 4.5 1.8S10.5 7 10.5 6" stroke={color} strokeWidth="1.2" fill="none" />
  </svg>
);

export interface ComponentTooltipProps {
  component: Component | null;
  isVisible: boolean;
  scale?: number;
}

/**
 * ComponentTooltip displays detailed component information on hover
 * Renders in a portal for clean positioning and z-index management
 */
export const ComponentTooltip: React.FC<ComponentTooltipProps> = ({
  component,
  isVisible,
  scale = 1,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<TooltipPlacement | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Calculate position when component or visibility changes
  useEffect(() => {
    if (!isVisible || !component) {
      setIsReady(false);
      setPosition(null);
      return;
    }

    // Wait for next frame to ensure DOM is updated
    requestAnimationFrame(() => {
      const tooltipSize = {
        width: tooltipRef.current?.offsetWidth || 400,
        height: tooltipRef.current?.offsetHeight || 600,
      };

      const newPosition = calculateTooltipPosition(component, tooltipSize, 12, scale);
      setPosition(newPosition);
      setIsReady(true);
    });
  }, [component, isVisible, scale]);

  if (!isVisible || !component) {
    return null;
  }

  const { icon, label } = getComponentTypeLabel(component.type);
  const relevantProperties = getRelevantProperties(component);
  const relevantStyles = getRelevantStyles(component);
  const dataCapable = isDataCapableType(component.type);
  const dataConnected = hasDataConnection(component);
  const dataInfo = getDataConnectionInfo(component);

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className={cn(
        'fixed z-[9999] w-auto',
        'backdrop-blur-md rounded-md shadow-xl',
        'transition-opacity duration-200',
        isReady ? 'opacity-100' : 'opacity-0'
      )}
      style={{
        left: position?.x || 0,
        top: position?.y || 0,
        pointerEvents: 'none', // Don't interfere with mouse events
        backgroundColor: `${CHARCOAL.DEFAULT}F2`, // 95% opacity
        color: WHITE,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: `${BORDER.dark}80`, // 50% opacity
      }}
      role="tooltip"
      aria-live="polite"
    >
      {/* Header - Component Identity */}
      <div
        className="px-3 py-1.5"
        style={{ borderBottom: `1px solid ${BORDER.dark}80` }}
      >
        <div className="flex items-center gap-1.5">
          <span className="text-base" aria-hidden="true">
            {icon}
          </span>
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-xs leading-tight" style={{ color: SEAFOAM_GREEN.DEFAULT }}>{label}</span>
            <span className="text-[10px] truncate leading-tight" style={{ color: MID_GRAY.light }}>{component.name}</span>
          </div>
          {dataCapable && (
            <div className="ml-auto flex-shrink-0" title={dataConnected ? 'Data connected' : 'No data connection'}>
              <DataIcon color={dataConnected ? SEAFOAM_GREEN.DEFAULT : MID_GRAY.DEFAULT} />
            </div>
          )}
        </div>
      </div>

      {/* Data Connection */}
      {dataInfo && (
        <div
          className="px-3 py-1.5"
          style={{ borderBottom: `1px solid ${BORDER.dark}80` }}
        >
          <div className="text-[9px] uppercase tracking-wide mb-1 font-semibold" style={{ color: SEAFOAM_GREEN.DEFAULT }}>
            Data
          </div>
          <div className="space-y-0.5 text-[10px]">
            <div className="flex gap-1.5 items-baseline">
              <span className="whitespace-nowrap" style={{ color: MID_GRAY.DEFAULT }}>source:</span>
              <span className="font-mono text-[9px]" style={{ color: WHITE }}>{dataInfo.schemaName}</span>
            </div>
            {(dataInfo.valueField || dataInfo.measures.length > 0) && (
              <div className="flex gap-1.5 items-baseline">
                <span className="whitespace-nowrap" style={{ color: MID_GRAY.DEFAULT }}>
                  {dataInfo.valueField ? 'field:' : 'measures:'}
                </span>
                <span className="font-mono text-[9px]" style={{ color: WHITE }}>
                  {dataInfo.valueField || dataInfo.measures.join(', ')}
                </span>
              </div>
            )}
            <div className="flex gap-1.5 items-baseline">
              <span className="whitespace-nowrap" style={{ color: MID_GRAY.DEFAULT }}>refresh:</span>
              <span className="font-mono text-[9px]" style={{ color: WHITE }}>
                {formatRefreshInterval(dataInfo.refreshInterval)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Properties */}
      {Object.keys(relevantProperties).length > 0 && (
        <div
          className="px-3 py-1.5"
          style={{ borderBottom: `1px solid ${BORDER.dark}80` }}
        >
          <div className="text-[9px] uppercase tracking-wide mb-1 font-semibold" style={{ color: MID_GRAY.DEFAULT }}>
            Properties
          </div>
          <div className="space-y-0.5 text-[10px]">
            {Object.entries(relevantProperties).slice(0, 8).map(([key, value]) => (
              <div key={key} className="flex gap-1.5 items-baseline">
                <span className="whitespace-nowrap" style={{ color: MID_GRAY.DEFAULT }}>{key}:</span>
                <span className="font-mono text-[9px]" style={{ color: WHITE }}>
                  {formatPropertyValue(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Styles */}
      {Object.keys(relevantStyles).length > 0 && (
        <div className="px-3 py-1.5">
          <div className="text-[9px] uppercase tracking-wide mb-1 font-semibold" style={{ color: MID_GRAY.DEFAULT }}>
            Styles
          </div>
          <div className="space-y-0.5 text-[10px]">
            {Object.entries(relevantStyles).slice(0, 8).map(([key, value]) => (
              <div key={key} className="flex gap-1.5 items-baseline">
                <span className="whitespace-nowrap" style={{ color: MID_GRAY.DEFAULT }}>{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                <span className="font-mono text-[9px]" style={{ color: WHITE }}>
                  {formatStyleValue(key, value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Locked/Hidden Status */}
      {(component.locked || component.hidden) && (
        <div
          className="px-3 py-1.5"
          style={{
            backgroundColor: `${CHARCOAL.light}4D`, // 30% opacity
            borderTop: `1px solid ${BORDER.dark}80`,
          }}
        >
          <div className="flex gap-2 text-[10px]">
            {component.locked && (
              <span className="flex items-center gap-0.5" style={{ color: WARNING.text }}>
                <span aria-hidden="true" className="text-xs">🔒</span>
                Locked
              </span>
            )}
            {component.hidden && (
              <span className="flex items-center gap-0.5" style={{ color: MID_GRAY.DEFAULT }}>
                <span aria-hidden="true" className="text-xs">👁️</span>
                Hidden
              </span>
            )}
          </div>
        </div>
      )}

      {/* Placement arrow indicator (visual only) */}
      {position && (
        <div
          className={cn(
            'absolute w-3 h-3 transform rotate-45',
            position.placement === 'top' && 'bottom-[-6px] left-1/2 -translate-x-1/2 border-b border-r',
            position.placement === 'bottom' && 'top-[-6px] left-1/2 -translate-x-1/2 border-t border-l',
            position.placement === 'left' && 'right-[-6px] top-1/2 -translate-y-1/2 border-t border-r',
            position.placement === 'right' && 'left-[-6px] top-1/2 -translate-y-1/2 border-b border-l'
          )}
          style={{
            backgroundColor: CHARCOAL.DEFAULT,
            borderColor: BORDER.dark,
          }}
          aria-hidden="true"
        />
      )}
    </div>
  );

  // Render in portal at document body for proper positioning
  return createPortal(tooltipContent, document.body);
};

/**
 * Memoized version for performance optimization
 */
export const MemoizedComponentTooltip = React.memo(
  ComponentTooltip,
  (prev, next) =>
    prev.component?.id === next.component?.id &&
    prev.isVisible === next.isVisible &&
    prev.scale === next.scale
);

export default ComponentTooltip;
